import React, { useEffect } from 'react';
import { Upload, Calendar } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useNavigate } from 'react-router-dom';
import { useUpload } from './useUpload';
import { FileSelector } from './FileSelector';
import { MetadataForm } from './MetadataForm';
import { SchedulingForm } from './SchedulingForm';
import { SubscriptionError } from './SubscriptionError';

const UploadModal = ({ isOpen, onClose, onUploadComplete }) => {
  const navigate = useNavigate();
  
  const {
    files,
    uploadMode,
    setUploadMode,
    uploading,
    uploadProgress,
    subscriptionError,
    setSubscriptionError,
    formData,
    setFormData,
    scheduleState,
    setScheduleState,
    handleFileSelect,
    removeFile,
    handleUpload,
    resetState
  } = useUpload(onClose, onUploadComplete);

  // Reset subscription error when modal opens
  useEffect(() => {
    if (isOpen) {
      setSubscriptionError(null);
    }
  }, [isOpen, setSubscriptionError]);

  const handleUpgradeClick = () => {
    onClose();
    navigate('/pricing');
  };

  const isScheduled = scheduleState.enabled && scheduleState.date && scheduleState.time;
  const canUpload = files.length > 0 && !uploading && (!scheduleState.enabled || (scheduleState.date && scheduleState.time));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-3xl bg-gray-900 text-white border-gray-700 max-h-[90vh] overflow-y-auto"
        data-testid="upload-modal"
      >
        {/* Show subscription error UI */}
        {subscriptionError ? (
          <SubscriptionError 
            error={subscriptionError}
            onClose={onClose}
            onUpgrade={handleUpgradeClick}
          />
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                <Upload className="w-6 h-6 text-yellow-400" />
                Upload Content
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6">
              {/* Upload Mode Selection */}
              <div className="flex gap-4">
                <Button
                  variant={uploadMode === 'single' ? 'default' : 'outline'}
                  onClick={() => setUploadMode('single')}
                  className={uploadMode === 'single' ? 'bg-gradient-to-r from-yellow-400 to-purple-500' : 'border-gray-600'}
                  data-testid="upload-mode-single"
                >
                  Single Upload
                </Button>
                <Button
                  variant={uploadMode === 'batch' ? 'default' : 'outline'}
                  onClick={() => setUploadMode('batch')}
                  className={uploadMode === 'batch' ? 'bg-gradient-to-r from-yellow-400 to-purple-500' : 'border-gray-600'}
                  data-testid="upload-mode-batch"
                >
                  Batch Upload
                </Button>
              </div>

              {/* File Selection */}
              <FileSelector 
                files={files}
                uploadMode={uploadMode}
                uploading={uploading}
                uploadProgress={uploadProgress}
                onFileSelect={handleFileSelect}
                onRemoveFile={removeFile}
              />

              {/* Metadata Form */}
              {files.length > 0 && (
                <MetadataForm 
                  formData={formData}
                  setFormData={setFormData}
                  uploading={uploading}
                  defaultFileName={files[0]?.name}
                />
              )}

              {/* Scheduling Section */}
              {files.length > 0 && (
                <SchedulingForm 
                  scheduleState={scheduleState}
                  setScheduleState={setScheduleState}
                  uploading={uploading}
                  onClose={onClose}
                />
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={uploading}
                  className="border-gray-600 text-gray-300"
                  data-testid="upload-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={!canUpload}
                  className="bg-gradient-to-r from-yellow-400 to-purple-500 hover:from-yellow-500 hover:to-purple-600"
                  data-testid="upload-submit-btn"
                >
                  {uploading ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin">⏳</span>
                      {isScheduled ? 'Scheduling...' : 'Uploading...'}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      {isScheduled ? <Calendar className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                      {isScheduled ? 'Schedule' : 'Upload'} {files.length} {files.length === 1 ? 'File' : 'Files'}
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UploadModal;
